import { useMemo } from "react";
import { Check } from "lucide-react";

// Mirrors backend/security.py::validate_password_strength so users get
// instant feedback instead of a server roundtrip / crash.
export const PASSWORD_RULES = [
  { key: "len",  label: "12+ characters",         test: (p) => p.length >= 12 },
  { key: "up",   label: "An uppercase letter",    test: (p) => /[A-Z]/.test(p) },
  { key: "low",  label: "A lowercase letter",     test: (p) => /[a-z]/.test(p) },
  { key: "num",  label: "A number",               test: (p) => /[0-9]/.test(p) },
  { key: "spec", label: "A special character",    test: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
];

export function passwordIsStrong(password) {
  return PASSWORD_RULES.every((r) => r.test(password || ""));
}

/**
 * Live password-rule checklist. Drop in below any password input on register
 * forms — turns each rule emerald as the user satisfies it. Returns null
 * when password is empty so we don't pre-shame fresh inputs.
 *
 * Props:
 *   password       string                                — current value
 *   "data-testid"  string                                — root testid
 */
export default function PasswordStrengthChecklist({
  password = "", "data-testid": tid = "password-rules",
}) {
  const ruleStatus = useMemo(
    () => PASSWORD_RULES.map((r) => ({ ...r, ok: r.test(password || "") })),
    [password],
  );
  if (!password) return null;
  return (
    <ul data-testid={tid} className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] pt-1">
      {ruleStatus.map((r) => (
        <li
          key={r.key}
          className={`flex items-center gap-1.5 ${r.ok ? "text-emerald-300" : "text-white/45"}`}
        >
          {r.ok ? (
            <Check size={12} />
          ) : (
            <span className="w-3 h-3 border border-current rounded-full inline-block" />
          )}
          {r.label}
        </li>
      ))}
    </ul>
  );
}
